import { useEffect, useRef } from 'react';

// Ultra-simple animation system with minimal performance impact
let globalObserver: IntersectionObserver | null = null;
let observerCount = 0;

const getGlobalObserver = () => {
  if (!globalObserver) {
    globalObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            element.style.opacity = '1';
            element.style.transform = 'translate3d(0, 0, 0)';
            globalObserver?.unobserve(element);
          }
        });
      },
      {
        rootMargin: '0px 0px -5% 0px',
        threshold: 0.01
      }
    );
  }
  return globalObserver;
};

export const useSimpleAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouch = window.matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints || 0) > 0;

    const disable = prefersReducedMotion || isTouch;

    if (disable) {
      // Show immediately; no observer, no transitions on mobile/reduced-motion
      element.style.opacity = '1';
      element.style.transform = 'none';
      element.style.transition = 'none';
      return;
    }

    const observer = getGlobalObserver();
    observerCount++;

    // Initialize with hidden state using transform3d for GPU acceleration
    element.style.opacity = '0';
    element.style.transform = 'translate3d(0, 40px, 0)';
    element.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';

    observer.observe(element);
    
    return () => {
      observer.unobserve(element);
      observerCount--;
      
      // Clean up global observer if no more elements are using it
      if (observerCount === 0 && globalObserver) {
        globalObserver.disconnect();
        globalObserver = null;
      }
    };
  }, []);

  return ref;
};

// Simple reveal component with reduced motion support
export const SimpleReveal = ({ 
  children, 
  className = '',
  delay = 0
}: { 
  children: React.ReactNode; 
  className?: string;
  delay?: number;
}) => {
  const ref = useSimpleAnimation();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouch = window.matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints || 0) > 0;
    if (delay > 0 && ref.current && !(prefersReducedMotion || isTouch)) {
      ref.current.style.transitionDelay = `${delay}ms`;
    }
  }, [delay]);

  // Respect reduced motion and touch devices (mobile)
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouch = window.matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints || 0) > 0;
    
    if ((prefersReducedMotion || isTouch) && ref.current) {
      ref.current.style.opacity = '1';
      ref.current.style.transform = 'none';
      ref.current.style.transition = 'none';
    }
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};